import type { Sanctuary, Site } from '../types';
import type { RegionId } from './oceans';
import type { Seafloor } from '../seafloor';
import type { WaterClimatology } from '../waterColumn';
import type { LightClimatology } from '../light';
import type { DailySst } from '../sstRecord';
import type { Reef } from '../reef';
import type { LarvalSettling, Spawning } from '../coral';
import { MAUI_NUI_SEAFLOOR } from './seafloor/mauiNui';
import { CAPE_SEAFLOOR } from './seafloor/cape';
import { MAUI_NUI_WATER, REGION_WATER } from './water/woa23';
import { MAUI_NUI_SST } from './climate/oisstMauiNui';
import { MAUI_NUI_LIGHT, REGION_LIGHT } from './light/light';
import { MAUI_NUI_REEF } from './reef/mauiNui';
import { MAUI_NUI_SETTLING } from './reef/connectivity';

/**
 * The places a sanctuary can be: one aquarium per sea (`GDD.md` section 2,
 * M10), each a real site. Static geography, like `oceans.ts`: code, not state.
 * A sanctuary in the world names its site by id (`Sanctuary.siteId`), and its
 * seafloor is found here.
 */

export type SiteId = 'maui_nui' | 'vavau' | 'cape_peninsula' | 'gulf_of_maine' | 'ningaloo' | 'antarctic_peninsula' | 'svalbard';

export interface SiteDef {
  /** The sanctuary's name when it is established. */
  name: string;
  regionId: RegionId;
  /** Where the aquarium is centered. Where there is a seafloor, its center. */
  site: Site;
  /** The aquarium's seafloor, or null where it has not been built yet. */
  seafloor: Seafloor | null;
  /** The aquarium's own water column, or null where it reads its region's. */
  water: WaterClimatology | null;
  /** The aquarium's own light, or null where it reads its region's (M33). */
  light: LightClimatology | null;
  /** The aquarium's own daily record of the surface against its normal, or null where it reads its region's (M24). */
  sst: DailySst | null;
  /** The aquarium's reef: its floor and its surveys (M45), or null where it has not been built. */
  reef: Reef | null;
  /** Where its coral's larvae settle among its islands (M68), or null where it has no reef. */
  settling: LarvalSettling | null;
}

/**
 * **Maui Nui, Hawaii, is the first sanctuary** (M11, the user's choice): the
 * waters between Maui, Lānaʻi, Molokaʻi and Kahoʻolawe, in the North
 * Pacific's low and mid latitude band. Argued in `GDD.md` section 10: the
 * best-documented tropical ocean there is (the Hawaii Ocean Time-series at
 * Station ALOHA, about 100 km north of Oʻahu, monthly since October 1988;
 * NOAA's reef fish and benthic surveys around the main islands), a fished and
 * degraded reef, and the core of the humpbacks' Hawaiian breeding grounds,
 * whose whales feed off Alaska.
 *
 * **Its position is pinned against the bathymetry** (M17): the 100 km square
 * holding the most of the four islands' shelf to 100 fathoms, the humpback
 * sanctuary's own edge (`scripts/data/seafloor.ts`). Its center lies over
 * Lānaʻi. MEASURED by the build: it holds all of Lānaʻi, Molokaʻi and
 * Kahoʻolawe, West Maui (27% of Maui's land) and the eastern end of Penguin
 * Bank, 2,398 km² of the shelf's 3,665; 769 km² lie beyond it to the west, the
 * rest of Penguin Bank, and 498 km² to the east, along East Maui. It was
 * Lahaina, about 20.88°N 156.68°W, approximate, from M11 to M16: about 24 km
 * east of the center.
 *
 * **Vava'u, Tonga** was the first opening (M1 to M10) and stays: saves of
 * version 1 and 2 were written there and must load as they were, and it is the
 * South Pacific's aquarium when that sea is built. Neiafu harbor, 18°39'S
 * 173°59'W, from one cruising guide (open, M2); its seafloor is built with it.
 */
export const SITES: Readonly<Record<SiteId, SiteDef>> = {
  maui_nui: {
    name: 'Maui Nui Sanctuary',
    regionId: 'north_pacific_low',
    site: { placeName: 'Maui Nui, Hawaii', latDeg: MAUI_NUI_SEAFLOOR.centerLatDeg, lonDeg: MAUI_NUI_SEAFLOOR.centerLonDeg },
    seafloor: MAUI_NUI_SEAFLOOR,
    water: MAUI_NUI_WATER,
    light: MAUI_NUI_LIGHT,
    sst: MAUI_NUI_SST,
    reef: MAUI_NUI_REEF,
    settling: MAUI_NUI_SETTLING,
  },
  vavau: {
    name: 'Vava\'u Sanctuary',
    regionId: 'south_pacific_low',
    // Neiafu harbor, from one cruising guide (ASSERTED, M2; open).
    site: { placeName: 'Vava\'u, Tonga', latDeg: -18.65, lonDeg: -173.983 },
    seafloor: null,
    water: null,
    light: null,
    sst: null,
    reef: null,
    settling: null,
  },
  /**
   * The other five seas' aquariums (M98, the user's brief, `AQUARIUM_REDESIGN.md` section 4; the sites `GDD.md`
   * section 2's, M10, Svalbard taken for the Arctic): none is a sanctuary until the player establishes one there.
   * The Cape Peninsula's square is built from GEBCO (`CAPE_SEAFLOOR`); the other four are placed at their sea's
   * named place, approximate (ASSERTED, M98: each sited when its sea is built, by the standing rule), with nothing
   * built of them yet.
   */
  cape_peninsula: {
    name: 'Cape Peninsula Sanctuary',
    regionId: 'south_atlantic_low',
    site: { placeName: 'Cape Peninsula, South Africa', latDeg: CAPE_SEAFLOOR.centerLatDeg, lonDeg: CAPE_SEAFLOOR.centerLonDeg },
    seafloor: CAPE_SEAFLOOR,
    water: null,
    light: null,
    sst: null,
    reef: null,
    settling: null,
  },
  gulf_of_maine: {
    name: 'Gulf of Maine Sanctuary',
    regionId: 'north_atlantic_high',
    site: { placeName: 'Gulf of Maine, United States', latDeg: 43, lonDeg: -68.5 },
    seafloor: null,
    water: null,
    light: null,
    sst: null,
    reef: null,
    settling: null,
  },
  ningaloo: {
    name: 'Ningaloo Sanctuary',
    regionId: 'indian_low',
    site: { placeName: 'Ningaloo Reef, Western Australia', latDeg: -22.7, lonDeg: 113.7 }, // approximate, ASSERTED (M98, above)
    seafloor: null,
    water: null,
    light: null,
    sst: null,
    reef: null,
    settling: null,
  },
  antarctic_peninsula: {
    name: 'Antarctic Peninsula Sanctuary',
    regionId: 'southern',
    site: { placeName: 'Antarctic Peninsula', latDeg: -64.5, lonDeg: -62.5 }, // approximate, ASSERTED (M98, above)
    seafloor: null,
    water: null,
    light: null,
    sst: null,
    reef: null,
    settling: null,
  },
  svalbard: {
    name: 'Svalbard Sanctuary',
    regionId: 'arctic',
    site: { placeName: 'Svalbard, Norway', latDeg: 78.2, lonDeg: 15 }, // approximate, ASSERTED (M98, above)
    seafloor: null,
    water: null,
    light: null,
    sst: null,
    reef: null,
    settling: null,
  },
};

/** The site a save names by its place, for saves written before sites had ids (M17). */
export function siteIdOfPlace(placeName: string): SiteId | null {
  for (const [id, def] of Object.entries(SITES) as Array<[SiteId, SiteDef]>) {
    if (def.site.placeName === placeName) return id;
  }
  return null;
}

/** A sanctuary's water column: its site's own, or its region's where the site has none yet. */
export function waterOf(sanctuary: Pick<Sanctuary, 'siteId' | 'regionId'>): WaterClimatology {
  return SITES[sanctuary.siteId].water ?? REGION_WATER[sanctuary.regionId];
}

/** A sanctuary's light: its site's own, or its region's (M33). */
export function lightOf(sanctuary: Pick<Sanctuary, 'siteId' | 'regionId'>): LightClimatology {
  return SITES[sanctuary.siteId].light ?? REGION_LIGHT[sanctuary.regionId];
}

/** A sanctuary's reef, or null where its site has none yet (M45). */
export function reefOf(sanctuary: Pick<Sanctuary, 'siteId'>): Reef | null {
  return SITES[sanctuary.siteId].reef;
}

/** A sanctuary's reef with where its larvae settle (M68), or null where its site has no reef. */
export function spawningOf(sanctuary: Pick<Sanctuary, 'siteId'>): Spawning | null {
  return spawningAt(sanctuary.siteId, SITES[sanctuary.siteId]);
}

/** A site's reef with its larvae's settling, or null without a reef: a reef without its settling would grow without recruits, so is refused (review M69: no site could reach the refusal, so no test held it). */
export function spawningAt(siteId: string, def: Pick<SiteDef, 'reef' | 'settling'>): Spawning | null {
  const { reef, settling } = def;
  if (!reef) return null;
  if (!settling) throw new Error(`${siteId}'s reef has no larval settling`);
  return { reef, settling };
}

/** A sanctuary's seafloor, or null where its site has none yet. */
export function seafloorOf(sanctuary: Pick<Sanctuary, 'siteId'>): Seafloor | null {
  return SITES[sanctuary.siteId].seafloor;
}
